<template>
  <div class="container mt-4 meeting-type-editor">
    <TopNav>
      <div class="d-flex" v-if="!isLoading">
        <a
          :href="getStudentPreviewURL(teacherStudioUrl, meetingType.id)"
          class="undecorated-router-link"
          target="_blank"
        >
          <NavItem icon="far fa-eye" :text="$t('GENERAL.BUTTON.PREVIEW')" />
        </a>
        <NavItem
          icon="far fa-save"
          :text="$t('GENERAL.BUTTON.SAVE')"
          :pulse="isDirty"
          :loading="isSaving"
          @click="save"
        />
      </div>
    </TopNav>
    <div class="text-center mt-4" v-if="isLoading">
      <HMSpinner />
    </div>
    <div v-else>
      <ValidationObserver ref="form">
        <h1 class="mt-4">{{ meetingType.name }}</h1>
        <section class="section-tabs mt-4">
          <HMTabs :value="selectedTab" @input="handleTabChange">
            <HMTab v-for="tab in tabs" :key="tab.value" :name="tab.value">
              <span>{{ tab.label }}</span>
            </HMTab>
          </HMTabs>
        </section>
        <section v-if="selectedTab === 'settings'">
          <form>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_NAME') }}</h3>
                <div class="smaller">
                  {{ $t('MARKETING.TEXT.MEETING_TYPE_NAME_DESCRIPTION') }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <HMTextInput
                  v-model="meetingType.name"
                  :placeholder="$t('MARKETING.PLACEHOLDERS.MEETING_TYPE_NAME')"
                  name="meeting type name"
                  rules="required|max:128"
                />
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_DURATION') }}</h3>
                <div class="smaller">
                  {{ $t('MARKETING.TEXT.MEETING_TYPE_DURATION_DESCRIPTION') }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <v-select
                  :options="durations()"
                  v-model="meetingType.duration_minutes"
                  :clearable="false"
                  :taggable="true"
                  :reduce="r => r.code"
                  @option:created="meetingTypeDurationOptionCreated"
                ></v-select>
              </div>
              <div class="col-12 col-md-6 pr-md-5 mt-4">
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_SLOTS_FREQUENCY') }}
                </h3>
                <div class="smaller">
                  {{
                    $t(
                      'MARKETING.TEXT.MEETING_TYPE_SLOTS_FREQUENCY_DESCRIPTION'
                    )
                  }}
                </div>
              </div>
              <div class="col-12 col-md-6 mt-4">
                <v-select
                  :options="durations()"
                  v-model="meetingType.slot_steps"
                  :clearable="true"
                  :taggable="true"
                  :reduce="r => r.code"
                  @option:created="meetingTypeDurationOptionCreated"
                ></v-select>
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_URL') }}</h3>
                <div class="smaller">
                  {{ $t('MARKETING.TEXT.MEETING_TYPE_URL_DESCRIPTION') }}
                  <a :href="getMeetingUrl" target="_blank">{{
                    getMeetingUrl
                  }}</a>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <HMTextInput
                  v-model="slug"
                  :placeholder="$t('MARKETING.PLACEHOLDERS.MEETING_TYPE_SLUG')"
                  rules="max:128"
                />
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4" v-if="teacherHasStripe">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_REQUIRE_PAYMENT') }}
                </h3>
              </div>
              <div class="col-12 col-md-6 text-right">
                <HMSwitch v-model="meetingType.require_payment" />
              </div>
            </div>
            <div
              class="row mt-2"
              v-if="teacherHasStripe && meetingType.require_payment"
            >
              <div class="col-12 col-md-6">
                <div class="smaller">
                  {{
                    $t(
                      'MARKETING.TEXT.MEETING_TYPE_REQUIRE_PAYMENT_DESCRIPTION'
                    )
                  }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <HMTextInput
                  type="number"
                  v-model="meetingType.price"
                  :placeholder="$t('MARKETING.PLACEHOLDERS.MEETING_TYPE_PRICE')"
                  rules="required|min_value:1"
                  name="session price"
                >
                  <template slot="append">{{ currency }}</template>
                </HMTextInput>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_DESCRIPTION') }}
                </h3>
                <div class="smaller">
                  {{
                    $t('MARKETING.TEXT.MEETING_TYPE_DESCRIPTION_DESCRIPTION')
                  }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <HMVueEditor
                  id="hm-vue-meeting-confirmation-message-editor"
                  class="w-100"
                  v-model="meetingType.description"
                  placeholder="Your booking confirmation message"
                />
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_IMAGE') }}
                </h3>
                <div class="smaller">
                  {{ $t('MARKETING.TEXT.MEETING_TYPE_IMAGE_DESCRIPTION') }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <HMImageUpload
                  inputName="cover-photo"
                  :title="$t('MARKETING.TEXT.UPLOAD_PHOTO')"
                  :description="
                    $t('PRODUCTS.TEXT.UPLOAD_IMAGE_TEXT', {
                      width: 1024,
                      height: 576,
                    })
                  "
                  :imageDescription="$t('MARKETING.TEXT.UPLOAD_PHOTO')"
                  v-model="meetingType.cover_photo"
                  :canvaWidth="1024"
                  :canvaHeight="576"
                  :focalPointPicker="true"
                />
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_CONFIRMATION_MESSAGE') }}
                </h3>
                <div class="smaller">
                  {{
                    $t(
                      'MARKETING.TEXT.MEETING_TYPE_CONFIRMATION_MESSAGE_DESCRIPTION'
                    )
                  }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <HMVueEditor
                  id="hm-vue-meeting-confirmation-message-editor"
                  class="w-100"
                  v-model="meetingType.confirmation_message"
                  placeholder="Your booking confirmation message"
                />
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_CONFIRMATION_MEDIA') }}
                </h3>
                <div class="smaller">
                  {{
                    $t(
                      'MARKETING.TEXT.MEETING_TYPE_CONFIRMATION_MEDIA_DESCRIPTION'
                    )
                  }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div
                  v-if="!meetingType.confirmation_media"
                  class="w-100 text-right"
                >
                  <HMButton color="primary" @click="showAddMediaForm">
                    <font-awesome-icon :icon="['far', 'file-upload']" />
                    <span class="text-nowrap ml-2">
                      {{ $t('PRODUCTS.BUTTON.ADD_MEDIA') }}
                    </span>
                  </HMButton>
                </div>
                <div v-else class="mt-3">
                  <HMFilePreview
                    :file="meetingType.confirmation_media.media"
                  ></HMFilePreview>
                  <div class="w-100 text-right mt-2">
                    <HMButton
                      color="primary"
                      @click="meetingType.confirmation_media = null"
                    >
                      <font-awesome-icon :icon="['far', 'trash']" />
                      <span class="text-nowrap ml-2">
                        {{ $t('PRODUCTS.BUTTON.REMOVE_MEDIA') }}
                      </span>
                    </HMButton>
                  </div>
                </div>
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_REMINDERS') }}</h3>
                <div class="smaller">
                  {{ $t('MARKETING.TEXT.MEETING_TYPE_REMINDERS_DESCRIPTION') }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <v-select
                  :options="getReminders()"
                  v-model="reminders"
                  multiple
                  :clearable="false"
                  :taggable="true"
                  :reduce="r => r.code"
                  @option:created="meetingTypeDurationOptionCreated"
                  :selectable="() => reminders.length < 5"
                ></v-select>
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-12 col-md-6 pr-md-5">
                <h3>
                  {{
                    $t(
                      'MARKETING.LABELS.MEETING_TYPE_RESCHEDULING_AND_CANCELING'
                    )
                  }}
                </h3>
                <div class="smaller">
                  {{
                    $t(
                      'MARKETING.TEXT.MEETING_TYPE_RESCHEDULING_AND_CANCELING_DESCRIPTION'
                    )
                  }}
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div
                  class="d-flex flex-row flex-nowrap align-items-center justify-content-between"
                >
                  <label class="small">{{
                    $t('MARKETING.LABELS.MEETING_TYPE_ALLOW_RESCHEDULING')
                  }}</label>
                  <HMSwitch v-model="meetingType.allow_reschedule" />
                </div>
                <div
                  class="d-flex flex-row flex-nowrap align-items-center justify-content-between mt-3"
                >
                  <label class="small">{{
                    $t('MARKETING.LABELS.MEETING_TYPE_ALLOW_CANCELLING')
                  }}</label>
                  <HMSwitch v-model="meetingType.allow_cancel" />
                </div>
              </div>
              <div class="col-12">
                <hr />
              </div>
            </div>
          </form>
        </section>
        <section v-if="selectedTab === 'availability'">
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_INSTRUCTORS') }}</h3>
              <div class="smaller">
                {{ $t('MARKETING.TEXT.MEETING_TYPE_INSTRUCTORS_DESCRIPTION') }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <v-select
                :options="allInstructors"
                multiple
                label="name"
                code="id"
                v-model="meetingType.instructors"
              />
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_ZOOM_CONNECTION') }}</h3>
              <div class="smaller">
                {{
                  $t('MARKETING.TEXT.MEETING_TYPE_ZOOM_CONNECTION_DESCRIPTION')
                }}
              </div>
            </div>
            <div class="col-12 col-md-6" v-if="hasZoomConnections">
              <v-select
                :options="zoomConnections"
                label="name"
                code="id"
                v-model="meetingType.zoom_connection"
                :reduce="c => c.id"
              />
              <div
                class="d-flex flex-row flex-nowrap align-items-center justify-content-between mt-3"
              >
                <label class="smaller">{{
                  $t('MARKETING.LABELS.MEETING_TYPE_ZOOM_AUTO_RECORDING')
                }}</label>
                <HMSwitch v-model="meetingType.zoom_auto_start_recording" />
              </div>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>
                {{ $t('MARKETING.LABELS.MEETING_TYPE_GOOGLE_MEET_CONNECTION') }}
              </h3>
              <div class="smaller">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_GOOGLE_MEET_CONNECTION_DESCRIPTION'
                  )
                }}
              </div>
            </div>
            <div class="col-12 col-md-6" v-if="hasGoogleMeetConnections">
              <v-select
                :options="googleMeetConnections"
                label="name"
                code="id"
                v-model="meetingType.google_meet_connection"
                :reduce="c => c.id"
              />
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_GOOGLE_CALENDAR') }}</h3>
              <div class="smaller">
                {{
                  $t('MARKETING.TEXT.MEETING_TYPE_GOOGLE_CALENDAR_DESCRIPTION')
                }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <v-select
                :options="googleCalendars"
                label="name"
                code="id"
                v-model="meetingType.google_calendar"
                :reduce="c => c.id"
              />
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div v-if="meetingType.google_calendar" class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <div class="d-flex flex-row">
                <h3 class="mr-3">
                  {{ $t('MARKETING.LABELS.MEETING_TYPE_ADDITIONAL_CALENDARS') }}
                </h3>
                <HMUpgradeLock
                  v-if="!hasAdditionalCalendars"
                  class="mb-3"
                  text="MARKETING.TEXT.MEETINGS_TYPE_MULTIPLE_CALENDARS_UPGRADE"
                />
              </div>
              <div class="smaller">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_ADDITIONAL_CALENDARS_DESCRIPTION'
                  )
                }}
              </div>
            </div>
            <div
              class="col-12 col-md-6"
              :class="{ 'text-right': !hasAdditionalCalendars }"
            >
              <v-select
                v-if="hasAdditionalCalendars"
                :options="nonPrimaryGoogleCalendars"
                :disabled="nonPrimaryGoogleCalendars.length === 0"
                multiple
                label="name"
                code="id"
                v-model="meetingType.google_calendars"
              />
              <HMButton
                v-else
                color="outline-light-gray"
                class="bg-white"
                @click="$router.push({ name: 'settings-adjust-plan' })"
              >
                <span>
                  <i class="far fa-plus mr-2 c-coral"></i>
                  {{ $t('GENERAL.BUTTON.UPGRADE_TO_ENABLE') }}
                </span>
              </HMButton>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-5 pr-md-5">
              <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_AVAILABILITY') }}</h3>
              <div class="smaller">
                {{ $t('MARKETING.TEXT.MEETING_TYPE_AVAILABILITY_DESCRIPTION') }}
              </div>
            </div>
            <div class="col-12 col-md-7">
              <HMAvailabilitySchedule
                v-model="meetingType.availability"
                :timezone="timezone"
              />
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_MEETING_BUFFERS') }}</h3>
              <div class="smaller">
                {{
                  $t('MARKETING.TEXT.MEETING_TYPE_MEETING_BUFFERS_DESCRIPTION')
                }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="d-flex flex-column justify-content-start">
                <label class="smaller">{{
                  $t('MARKETING.LABELS.TIME_TO_BUFFER_BEFORE_MEETINGS')
                }}</label>
                <v-select
                  :options="buffers()"
                  v-model="meetingType.buffer_before"
                  :clearable="false"
                  :taggable="true"
                  :reduce="r => r.code"
                  @option:created="meetingTypeDurationOptionCreated"
                />
              </div>
              <div class="d-flex flex-column justify-content-start mt-3">
                <label class="smaller">{{
                  $t('MARKETING.LABELS.TIME_TO_BUFFER_AFTER_MEETINGS')
                }}</label>
                <v-select
                  :options="buffers()"
                  v-model="meetingType.buffer_after"
                  :clearable="false"
                  :taggable="true"
                  :reduce="r => r.code"
                  @option:created="meetingTypeDurationOptionCreated"
                />
              </div>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>
                {{ $t('MARKETING.LABELS.MEETING_TYPE_MINIMUM_BOOKING_NOTICE') }}
              </h3>
              <div class="smaller">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_MINIMUM_BOOKING_NOTICE_DESCRIPTION'
                  )
                }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <HMTextInput
                type="number"
                v-model="meetingType.minimum_booking_notice_hours"
                rules="min_value:0"
              >
                <template slot="append">{{
                  $t('MARKETING.LABELS.HOURS')
                }}</template>
              </HMTextInput>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>
                {{
                  $t('MARKETING.LABELS.MEETING_TYPE_MAXIMUM_MEETINGS_PER_DAY')
                }}
              </h3>
              <div class="smaller">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_MAXIMUM_MEETINGS_PER_DAY_DESCRIPTION'
                  )
                }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <HMTextInput
                type="number"
                v-model="meetingType.max_per_day"
                rules="min_value:0"
              >
              </HMTextInput>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12 col-md-6 pr-md-5">
              <h3>
                {{
                  $t('MARKETING.LABELS.MEETING_TYPE_MAXIMUM_DAYS_IN_ADVANCE')
                }}
              </h3>
              <div class="smaller">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_MAXIMUM_DAYS_IN_ADVANCE_DESCRIPTION'
                  )
                }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <HMTextInput
                type="number"
                v-model="meetingType.max_days_in_advance"
                rules="min_value:0"
              >
                <template slot="append">{{
                  $t('MARKETING.LABELS.DAYS')
                }}</template>
              </HMTextInput>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
        </section>
        <section v-if="selectedTab === 'form'">
          <div class="row mt-4">
            <div class="col-12 col-md-5 pr-md-5">
              <h3>{{ $t('MARKETING.LABELS.MEETING_TYPE_BOOKING_FORM') }}</h3>
              <div class="smaller">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_BOOKING_FORM_DESCRIPTION_DEFAULT_QUESTIONS'
                  )
                }}
              </div>
              <div
                class="mt-2 border rounded p-3 w-100 question-outline default"
                v-for="question in fixedQuestions"
                :key="question.label"
              >
                <div
                  class="d-flex flex-row flex-nowrap justify-content-between align-items-center"
                >
                  <div>{{ question.label }}*</div>
                </div>
              </div>
              <hr />
              <div class="smaller" v-if="questions.length > 1">
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_BOOKING_FORM_DESCRIPTION_DRAG_AND_DROP'
                  )
                }}
              </div>
              <div class="smaller" v-else>
                {{
                  $t(
                    'MARKETING.TEXT.MEETING_TYPE_BOOKING_FORM_DESCRIPTION_ADD_QUESTIONS'
                  )
                }}
              </div>
              <draggable :delay="draggableDelay" tag="div" v-model="questions">
                <div
                  class="mt-2 border rounded p-3 w-100 question-outline"
                  :class="{ active: selectedQuestion == question }"
                  v-for="question in questions"
                  :key="question.label"
                  @click="selectedQuestion = question"
                >
                  <div
                    class="d-flex flex-row flex-nowrap justify-content-between align-items-center"
                  >
                    <v-clamp autoresize :max-lines="2">
                      {{ question.label }}
                    </v-clamp>
                    <div
                      v-tooltip="{
                        content: !isSessionStarted
                          ? $t('MARKETING.TOOLTIPS.DELETE_QUESTION')
                          : '',
                        delay: 100,
                        placement: 'top',
                        container: 'body',
                        trigger: 'click hover',
                      }"
                      @click.stop="removeQuestion(question)"
                    >
                      <i class="far fa-trash-alt delete"></i>
                    </div>
                  </div>
                </div>
              </draggable>
              <HMButton class="mt-3" color="primary">
                <font-awesome-icon :icon="['far', 'plus']" />
                <span class="text-nowrap ml-2" @click="addQuestion">
                  {{ $t('MARKETING.BUTTONS.ADD_NEW_QUESTION') }}
                </span>
              </HMButton>
            </div>
            <div class="col-12 col-md-7" v-if="selectedQuestion">
              <h4>{{ $t('MARKETING.LABELS.QUESTION') }}</h4>
              <HMTextInput
                class="mt-3 mb-2"
                v-model="selectedQuestion.label"
              ></HMTextInput>
              <div
                class="d-flex flex-row justify-content-between align-items-center flex-nowrap"
              >
                <label class="c-charcoal m-0 mr-4">{{
                  $t('MARKETING.LABELS.REQUIRED')
                }}</label>
                <HMSwitch v-model="selectedQuestion.required" />
              </div>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
        </section>
        <section v-if="selectedTab === 'summary'">
          <div class="row mt-4">
            <div class="col-12">
              <div
                class="d-flex flex-row justify-content-between align-items-center"
              >
                <h3>
                  {{ $t('MARKETING.LABELS.MEETING_IS') }}
                  <span
                    :class="{
                      published: meetingType.active,
                      draft: !meetingType.active,
                    }"
                  >
                    {{
                      $tc('MARKETING.LABELS.STATUS_VARIANT', meetingType.active)
                    }}
                  </span>
                </h3>
                <HMButton
                  v-if="meetingType.active"
                  class="mt-3"
                  color="outline-gray"
                  @click="openShareModal"
                >
                  <i class="fa fa-send c-brand-color mr-2"></i>
                  {{ $t('MARKETING.LABELS.SHARE') }}
                </HMButton>
              </div>
              <div v-if="meetingType.active">
                <div
                  v-if="hasValidCustomDomain"
                  class="form-group mb-0 d-flex flex-row justify-content-start align-items-center mt-3"
                >
                  <span class="mr-3">
                    {{ $t('MARKETING.LABELS.USE_CUSTOM_DOMAIN_URL') }}
                  </span>
                  <HMSwitch v-model="useCustomDomainUrl" />
                </div>
                <span id="meetingURL">{{ getMeetingUrl }}</span>
                <font-awesome-icon
                  :icon="['far', 'link']"
                  class="ml-2 c-brand-color copy pointer"
                  @click="copyMeetingURL"
                />
              </div>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12">
              <div
                class="d-flex flex-row justify-content-between align-items-center"
              >
                <h3>{{ $t('MARKETING.LABELS.PUBLISHED_STATUS') }}</h3>
                <HMToggleButtonGroup v-model="meetingType.active">
                  <template slot="falseState">
                    <span>
                      {{ $tc('MARKETING.LABELS.STATUS_VARIANT', 0) }}
                    </span>
                  </template>
                  <template slot="trueState">
                    <span>
                      {{ $tc('MARKETING.LABELS.STATUS_VARIANT', 1) }}
                    </span>
                  </template>
                </HMToggleButtonGroup>
              </div>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-12">
              <div
                class="d-flex flex-row justify-content-between align-items-center"
              >
                <h3>{{ $t('MARKETING.LABELS.VISIBLE_IN_SHOP') }}</h3>
                <HMToggleButtonGroup v-model="meetingType.visible">
                  <template slot="falseState">
                    <span>
                      {{ $tc('MARKETING.LABELS.VISIBLE_IN_SHOP_VARIANT', 0) }}
                    </span>
                  </template>
                  <template slot="trueState">
                    <span>
                      {{ $tc('MARKETING.LABELS.VISIBLE_IN_SHOP_VARIANT', 1) }}
                    </span>
                  </template>
                </HMToggleButtonGroup>
              </div>
            </div>
            <div class="col-12">
              <hr />
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-sm-12">
              <hr />
            </div>
            <div class="col-sm-12">
              <div
                class="www-card d-flex flex-row justify-content-between align-items-start flex-wrap rounded w-100 p-4"
              >
                <div class="d-flex mr-md-4 flex-0">
                  <img
                    src="@/assets/images/wellwellwell-session-1.png"
                    class="mr-3"
                  />
                  <img src="@/assets/images/wellwellwell-session-2.png" />
                </div>
                <div class="d-flex flex-column flex-1 mr-4 mt-4 mt-md-0">
                  <img
                    class="www-logo"
                    src="@/assets/images/wellwellwell-logo.png"
                  />
                  <div
                    class="c-charcoal mt-3"
                    v-html="
                      sanitize(
                        $t(
                          'MARKETING.TEXT.WANT_US_TO_MARKET_THIS_SESSION_FOR_YOU'
                        )
                      )
                    "
                  ></div>
                </div>
                <div class="d-flex flex-0">
                  <HMSwitch v-model="meetingType.is_www_expanded" />
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-4" v-if="meetingType.is_www_expanded">
            <div class="col-12">
              <label
                v-html="sanitize($t('MARKETING.LABELS.WWW_MEETING_TYPE_PRICE'))"
              ></label>
              <HMTextInput
                type="number"
                v-model="meetingType.www_price"
                :rules="`required${canPostFreeWWWEvent ? '' : '|min_value:5'}`"
                :name="$t('MARKETING.LABELS.WWW_MEETING_TYPE_PRICE_FIELD_NAME')"
              >
                <template slot="prepend">USD</template>
              </HMTextInput>
            </div>
            <div class="col-12">
              <label
                v-html="sanitize($t('MARKETING.TEXT.WWW_SESSION_CATEGORIES'))"
              ></label>
              <v-select
                :placeholder="$t('EVENTS.LABEL.SELECT_ONE_OR_MORE_CATEGORIES')"
                :options="meetingTypeCategories"
                :multiple="true"
                label="name"
                code="id"
                v-model="meetingType.www_session_categories"
              />
            </div>
            <div class="col-12 mt-4">
              <div
                class="form-group email-providers flodesk"
                v-if="flodeskSegments.length && isUsingFlodesk"
              >
                <label>{{ $t('PRODUCTS.LABELS.FLODESK_SEGMENT') }}</label>
                <v-select
                  :options="flodeskSegments"
                  label="name"
                  code="id"
                  v-model="meetingType.www_flodesk_segment"
                  :reduce="segment => segment.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.FLODESK_SEGMENT_DESCRIPTION')
                }}</span>
              </div>
              <div
                class="form-group email-providers convertkit"
                v-if="convertKitTags.length && isUsingConvertKit"
              >
                <label>{{ $t('PRODUCTS.LABELS.CONVERTKIT_TAG') }}</label>
                <v-select
                  :options="convertKitTagsMapped"
                  label="name"
                  code="id"
                  v-model="meetingType.www_convertkit_tag"
                  :reduce="tag => tag.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.CONVERTKIT_TAG_DESCRIPTION')
                }}</span>
              </div>
              <div
                class="form-group email-providers mailchimp"
                v-if="mailChimpLists.length && isUsingMailChimp"
              >
                <label>{{ $t('PRODUCTS.LABELS.MAILCHIMP_LIST') }}</label>
                <v-select
                  :options="mailChimpLists"
                  label="name"
                  code="id"
                  v-model="meetingType.www_mailchimp_list"
                  :reduce="list => list.id"
                />
                <span class="small">{{
                  $t('EVENTS.TEXT.MAILCHIMP_LIST_DESCRIPTION')
                }}</span>
                <HMTextInput
                  v-if="meetingType.mailchimp_list"
                  class="mt-3"
                  type="text"
                  v-model="meetingType.www_mailchimp_tag"
                  :placeholder="$t('PRODUCTS.LABELS.OPTIONAL_MAILCHIMP_TAG')"
                  :label="$t('EVENTS.TEXT.USE_MAILCHIMP_TAG')"
                  name="Mailchimp tag"
                />
              </div>
            </div>
            <div class="col-12 mb-3">
              <h4>
                {{ $t('MARKETING.LABELS.UPLOAD_SESSION_IMAGES') }}
              </h4>
              <p>
                {{ $t('EVENTS.TEXT.WE_REVIEW_EVERY_PHOTO') }}
              </p>
              <ul>
                <li>{{ $t('EVENTS.TEXT.HIGH_RESOLUTION') }}</li>
                <li>{{ $t('EVENTS.TEXT.NO_EXPLICIT_OR_OFFENSIVE_IMAGES') }}</li>
                <li>{{ $t('EVENTS.TEXT.CLEAR_NOT_BLURRY') }}</li>
                <li>{{ $t('EVENTS.TEXT.WELL_LIT_NOT_TOO_DARK') }}</li>
                <li>{{ $t('EVENTS.TEXT.NO_HEAVY_FILTERS') }}</li>
                <li>{{ $t('EVENTS.TEXT.NO_TEXT_OVERLAYS') }}</li>
                <li>{{ $t('EVENTS.TEXT.SINGLE_PHOTOS') }}</li>
                <li>
                  {{
                    $t(
                      'EVENTS.TEXT.PHOTOS_SHOULD_ATTEMPT_TO_DESCRIBE_AN_INTERESTING_ELEMENT'
                    )
                  }}
                </li>
                <li>{{ $t('EVENTS.TEXT.NO_SCREENSHOTS') }}</li>
              </ul>
              <p>
                {{ $t('EVENTS.TEXT.IF_YOU_ARE_UNABLE_TO_FIND_IMAGERY') }}
              </p>
              <p>
                {{
                  $t('EVENTS.TEXT.IF_IMAGERY_DOES_NOT_FOLLOW_THE_REQUIREMENTS')
                }}
              </p>
              <p class="d-flex flex-row align-items-center mt-4">
                <HMCheckBox
                  class="mr-3"
                  v-model="meetingType.agreed_to_www_requirements"
                />
                <span
                  @click="
                    meetingType.agreed_to_www_requirements = !meetingType.agreed_to_www_requirements
                  "
                >
                  {{
                    $t(
                      'MARKETING.TEXT.I_AGREE_TO_ADHERE_TO_THE_IMAGE_REQUIREMENTS'
                    )
                  }}
                </span>
              </p>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <HMImageUpload
                  inputName="www-photo1"
                  :title="`${$t('MARKETING.TEXT.UPLOAD_SESSION_PHOTO')} 1`"
                  :description="$t('EVENTS.TEXT.WWW_EVENT_PHOTOS_TEXT')"
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="meetingType.www_photo1"
                  :canvaWidth="1024"
                  :canvaHeight="1024"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <HMImageUpload
                  inputName="www-photo2"
                  :title="`${$t('MARKETING.TEXT.UPLOAD_SESSION_PHOTO')} 2`"
                  :description="$t('EVENTS.TEXT.WWW_EVENT_PHOTOS_TEXT')"
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="meetingType.www_photo2"
                  :canvaWidth="1024"
                  :canvaHeight="1024"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="form-group">
                <HMImageUpload
                  inputName="www-photo3"
                  :title="`${$t('MARKETING.TEXT.UPLOAD_SESSION_PHOTO')} 3`"
                  :description="$t('EVENTS.TEXT.WWW_EVENT_PHOTOS_TEXT')"
                  :imageDescription="$t('PRODUCTS.LABELS.COVER_PHOTO')"
                  v-model="meetingType.www_photo3"
                  :canvaWidth="1024"
                  :canvaHeight="1024"
                  :focalPointPicker="true"
                />
              </div>
            </div>
            <div
              class="col-sm-12 mb-3"
              v-if="meetingType.www_admin_review_notes"
            >
              <h4>
                {{ $t('EVENTS.LABEL.MARVELOUS_REVIEW_NOTES') }}
              </h4>
              <div class="alert alert-light mb-1">
                {{ meetingType.www_admin_review_notes }}
              </div>
              <span class="d-block text-right">
                {{
                  $formatDate(meetingType.www_admin_review_date, 'date-word')
                }}
              </span>
            </div>
            <div class="col-12 justify-content-between d-flex flex-row">
              <h4>
                {{ $t('EVENTS.LABEL.PUBLISH_ON_WELLWELLWELL') }}
              </h4>
              <HMSwitch
                v-model="meetingType.is_www_session"
                :disabled="
                  !isEligibleForWWWMeetingTypePublishing &&
                  !meetingType.is_www_session
                "
                v-tooltip="{
                  content:
                    !isEligibleForWWWMeetingTypePublishing &&
                    !meetingType.is_www_session
                      ? $t('EVENTS.LABEL.PLEASE_SEE_FEEDBACK_BELOW')
                      : null,
                  delay: 100,
                  placement: 'top',
                  container: 'body',
                  trigger: 'click hover',
                }"
              />
            </div>
            <div
              v-if="!isEligibleForWWWMeetingTypePublishing"
              class="col-12 mt-3"
            >
              <div class="alert alert-danger">
                {{ $t('EVENTS.TEXT.TO_POST_ON_WELLWELLWELL_YOU_NEED') }}
                <ul class="mb-0">
                  <li
                    v-if="!hasPaymentInfo"
                    v-html="
                      sanitize(
                        $t(
                          'EVENTS.TEXT.RECONNECT_YOUR_STRIPE_ACCOUNT_TO_PUBLISH_ON_WELLWELLWELL'
                        )
                      )
                    "
                  ></li>
                  <li v-if="!hasWWWPricing">
                    {{ $t('EVENTS.TEXT.ADD_WWW_PRICING') }}
                  </li>
                  <li v-if="!hasInstructors">
                    {{ $t('EVENTS.TEXT.ADD_INSTRUCTOR') }}
                  </li>
                  <li
                    v-if="!hasInstructorBio"
                    v-html="sanitize($t('EVENTS.TEXT.ADD_INSTRUCTOR_BIO'))"
                  ></li>
                  <li v-if="!hasEventImages">
                    {{ $t('EVENTS.TEXT.ADD_EVENT_IMAGES') }}
                  </li>
                  <li v-if="!hasEventDescription">
                    {{ $t('EVENTS.TEXT.ADD_EVENT_DESCRIPTION') }}
                  </li>
                  <li v-if="!meetingType.agreed_to_www_requirements">
                    {{
                      $t(
                        'EVENTS.TEXT.YOU_NEED_TO_AGREE_TO_THE_WWW_REQUIREMENTS'
                      )
                    }}
                  </li>
                  <li v-if="!hasZoomOrGoogleMeetConnection">
                    {{
                      $t('MARKETING.TEXT.ADD_ZOOM_OR_GOOGLE_MEET_CONNECTION')
                    }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import DirtyMixin from '../../../mixins/DirtyMixin';
import { errorToMessage } from '../../../../core/utils/errors';
import { getStudentViewURL } from '../../../router/custom-domain-utils';
import { mapGetters } from 'vuex';
import TopNav from '../../../components/navigation/TopNav';
import NavItem from '../../../components/navigation/NavItem';
import HMTextInput from '../../../components/common/HMTextInput';
import HMVueEditor from '../../../components/common/HMVueEditor';
import { sanitize } from '@/core/utils/utils';

import {
  buffers,
  durations,
  meetingTypeDurationOptionCreated,
  reminders,
} from '../../../../core/utils/meetings';
import slugify from '@/core/utils/slugify';
import MediaFormModal from '../../../components/media-library/MediaFormModal';
import LibraryAddMedia from '../media-library/LibraryAddMedia';
import TeacherPermissionsMixin from '../../../mixins/TeacherPermissionsMixin';
import { notify } from '../../../utils/notify';
import HMButton from '../../../components/common/HMButton';
import draggable from 'vuedraggable';
import { draggableDelay } from '@/web/utils/draggable';
import VClamp from 'vue-clamp';
import HMShareLinkModal from '../../../components/common/HMShareLinkModal';
import { ValidationObserver } from 'vee-validate';
import { scrollToFirstFormError } from '@/core/utils/errors';

export default {
  name: 'MeetingTypeEditor',
  components: {
    HMButton,
    HMTextInput,
    TopNav,
    NavItem,
    draggable,
    VClamp,
    HMVueEditor,
    ValidationObserver,
  },
  mixins: [DirtyMixin, TeacherPermissionsMixin],
  computed: {
    ...mapGetters({
      teacherStudioUrl: 'user/teacherStudioUrl',
      teacherMagicCode: 'user/teacherMagicCode',
      allInstructors: 'mediaLibrary/instructors',
      googleCalendars: 'setting/googleCalendars',
      zoomConnections: 'setting/zoomConnections',
      googleMeetConnections: 'setting/googleMeetConnections',
      timezone: 'user/timezone',
      domain: 'setting/domain',
      teacher: 'user/teacher',
      currency: 'user/teacherCurrency',
      teacherHasStripe: 'user/teacherHasStripe',
      canPostFreeWWWEvent: 'user/canPostFreeWWWEvent',
      meetingTypeCategories: 'marketing/meetingTypeCategories',
      flodeskSegments: 'setting/flodeskSegments',
      mailChimpLists: 'setting/mailChimpLists',
      convertKitTags: 'setting/convertKitTags',
      isUsingFlodesk: 'setting/isUsingFlodesk',
      isUsingConvertKit: 'setting/isUsingConvertKit',
      isUsingMailChimp: 'setting/isUsingMailChimp',
      paymentInfo: 'setting/paymentInfo',
    }),
    hasAdditionalCalendars() {
      return this.teacher.max_google_calendar_connections > 1;
    },
    hasGoogleMeetConnections() {
      return this.googleMeetConnections?.length;
    },
    hasZoomConnections() {
      return this.zoomConnections?.length;
    },
    nonPrimaryGoogleCalendars() {
      return this.googleCalendars.filter(
        c =>
          c.id !== this.meetingType.google_calendar &&
          c.id !== this.meetingType.google_calendar?.id
      );
    },
    fixedQuestions() {
      return [
        {
          label: 'Full Name',
          required: true,
        },
        {
          label: 'Email Address',
          required: true,
        },
      ];
    },
    questions: {
      get() {
        if (!this.meetingType?.form || !this.meetingType.form.questions)
          return [];
        return (
          [...this.meetingType.form.questions].sort(
            (p1, p2) => p1.position - p2.position
          ) ?? []
        );
      },
      set(values) {
        values.forEach((v, i) => {
          if (v.position !== i) {
            v.position = i;
          }
        });
      },
    },
    useCustomDomainUrl: {
      get() {
        return this.$store.getters['setting/useCustomDomainUrl'];
      },
      set(value) {
        this.$store.dispatch('setting/setUseCustomDomainUrl', value);
      },
    },
    hasValidCustomDomain() {
      if (!this.domain) return false;
      if (!this.domain.success) return false;

      return true;
    },
    domainName() {
      return this.domain?.domain;
    },
    tabs() {
      return [
        {
          value: 'settings',
          label: this.$t('MARKETING.TABS.SETTINGS'),
        },
        {
          value: 'availability',
          label: this.$t('MARKETING.TABS.AVAILABILITY'),
        },
        {
          value: 'form',
          label: this.$t('MARKETING.TABS.FORM'),
        },
        {
          value: 'summary',
          label: this.$t('MARKETING.TABS.SUMMARY'),
        },
      ];
    },
    getMeetingUrl() {
      if (this.useCustomDomainUrl && this.hasValidCustomDomain) {
        return encodeURI(
          `https://${this.domainName}/meet/${this.meetingType.slug}`
        );
      }
      return getStudentViewURL(
        this.teacherStudioUrl,
        `/meet/${this.meetingType.slug}`,
        null
      );
    },
    slug: {
      get() {
        return this.meetingType.slug || '';
      },
      set(text) {
        this.$set(this.meetingType, 'slug', slugify(text));
      },
    },
    reminders: {
      get() {
        if (!this.meetingType.reminders) return [];
        return this.meetingType.reminders.split(',').map(e => {
          return parseInt(e);
        });
      },
      set(reminders) {
        console.log('set reminders', reminders);
        console.log('reminders=', reminders.join(','));
        this.$set(this.meetingType, 'reminders', reminders.join(','));
      },
    },
    isEligibleForWWWMeetingTypePublishing() {
      return (
        this.hasPaymentInfo &&
        this.hasInstructors &&
        this.hasInstructorBio &&
        this.hasEventDescription &&
        this.hasEventImages &&
        this.hasWWWPricing &&
        this.meetingType?.agreed_to_www_requirements &&
        this.hasZoomOrGoogleMeetConnection
      );
    },
    hasWWWPricing() {
      return this.meetingType?.www_price;
    },
    hasPaymentInfo() {
      return (
        this.paymentInfo?.payment_gateway === 'stripe' &&
        !!this.paymentInfo?.stripe_user_id
      );
    },
    hasInstructors() {
      return this.meetingType?.instructors?.length;
    },
    hasInstructorBio() {
      return this.meetingType?.instructors?.every(
        instructor => instructor.bio || instructor.bio_new
      );
    },
    hasEventDescription() {
      return this.meetingType?.description?.length > 0;
    },
    hasZoomOrGoogleMeetConnection() {
      return (
        this.meetingType?.zoom_connection ||
        this.meetingType?.google_meet_connection
      );
    },
    hasEventImages() {
      return (
        this.meetingType?.www_photo1 ||
        this.meetingType?.www_photo2 ||
        this.meetingType?.www_photo3
      );
    },
    convertKitTagsMapped() {
      return this.convertKitTags.map(tag => {
        return {
          ...tag,
          id: `${tag.id}`,
        };
      });
    },
  },
  data() {
    return {
      selectedTab: 'settings',
      previousTab: 'settings',
      meetingType: null,
      isLoading: true,
      isSaving: false,
      selectedQuestion: null,
      draggableDelay,
    };
  },
  methods: {
    sanitize,
    durations() {
      return durations();
    },
    getReminders() {
      return reminders();
    },
    buffers() {
      return buffers();
    },
    meetingTypeDurationOptionCreated(option) {
      meetingTypeDurationOptionCreated(option);
    },
    getStudentPreviewURL(studioUrl) {
      return getStudentViewURL(
        studioUrl,
        `/meet/${this.meetingType.slug}`,
        null
      );
    },
    async loadData() {
      const meetingTypeId = this.$route.params.id;
      try {
        this.isLoading = true;
        this.meetingType = await this.$store.dispatch(
          'marketing/getMeetingType',
          meetingTypeId
        );
        this.selectedQuestion = this.questions.length
          ? this.questions[0]
          : null;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not load meeting type',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    async save() {
      this.$refs.form.validate();
      const invalid = this.$refs.form?.flags?.invalid;
      if (invalid) {
        setTimeout(() => {
          scrollToFirstFormError();
        });
        return;
      }

      try {
        this.isSaving = true;
        const meetingType = { ...this.meetingType };
        meetingType.www_photo1 = meetingType.www_photo1?.id;
        meetingType.www_photo2 = meetingType.www_photo2?.id;
        meetingType.www_photo3 = meetingType.www_photo3?.id;

        this.meetingType = await this.$store.dispatch(
          'marketing/updateMeetingType',
          this.meetingType
        );
        setTimeout(this.clearDirty, 100);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('NOTIFICATIONS.TEXT.MEETING_TYPE_SUCCESSFULLY_UPDATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not save meeting type',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isSaving = false;
      }
    },
    async showAddMediaForm() {
      this.$modal.show(
        MediaFormModal,
        {
          showSelectMedia: true,
          multiSelect: false,
          showEditMedia: false,
          videoUploadAllowed: this.videoUploadAllowed,
        },
        { classes: ['vm--inline-modal'], clickToClose: false },
        {
          'media-selected': async content => {
            console.log('selected media', content);
            this.meetingType.confirmation_media = content[0];
          },
          'media-added': media => {
            this.showEditMedia(media);
          },
        }
      );
    },
    showEditMedia(media) {
      this.$modal.show(
        LibraryAddMedia,
        {
          medaiData: media,
          showAsModal: true,
        },
        { classes: ['vm--inline-modal'] },
        {
          'media-saved': async media => {
            console.log('media saved', media);
            this.meetingType.confirmation_media = media;
          },
        }
      );
    },
    async loadCustomDomainInfo() {
      try {
        if (!this.hasValidCustomDomain) {
          await this.$store.dispatch('setting/getDomain');
        }
      } catch (_) {
        // it's ok, domain either not allowed or not found
      }
    },
    openShareModal() {
      this.$modal.show(HMShareLinkModal, {
        title: this.$t('MARKETING.TITLES.SHARE_MEETING'),
        url: this.getMeetingUrl,
        message: 'Book a meeting with me using this link',
      });
    },
    copyMeetingURL() {
      this.$copyText(this.getMeetingUrl).then(() => {
        notify(this.$t('PRODUCTS.TOAST.COPIED'), this.getMeetingUrl);
      });
    },
    addQuestion() {
      this.selectedQuestion = {
        type: 'text',
        label: 'A new question',
        required: true,
        position: this.questions.length,
      };

      this.$set(this.meetingType.form, 'questions', [
        ...this.meetingType.form.questions,
        this.selectedQuestion,
      ]);
    },
    removeQuestion(question) {
      this.$set(this.meetingType.form, 'questions', [
        ...this.meetingType.form.questions.filter(q => q !== question),
      ]);
      this.selectedQuestion = null;
    },
    async handleTabChange(newTab) {
      console.log('handleTabChange', newTab);
      // Validate form if we're leaving the settings tab
      this.$refs.form?.validate();

      const invalid = this.$refs.form?.flags?.invalid;
      if (invalid) {
        setTimeout(() => {
          scrollToFirstFormError();
        });
      } else {
        this.selectedTab = newTab;
      }
    },
  },
  async mounted() {
    this.$store.dispatch('mediaLibrary/getInstructors');
    this.$store.dispatch('setting/getZoomConnections');
    this.$store.dispatch('setting/getGoogleMeetConnections');
    this.$store.dispatch('setting/getGoogleCalendarConnections');

    this.$store.dispatch('setting/getEmailProvider');
    this.$store.dispatch('setting/getFlodeskSegments');
    this.$store.dispatch('setting/getMailChimpLists');
    this.$store.dispatch('setting/getConvertKitTags');
    this.$store.dispatch('setting/getPaymentInfo');
    this.$store.dispatch('marketing/getMeetingTypesCategories');

    this.loadCustomDomainInfo();
    await this.loadData();
  },
  watch: {
    // create deep watch on meetingType
    meetingType: {
      handler: function (meetingType, oldMeetingType) {
        if (meetingType && oldMeetingType) {
          this.setDirty();
        }
        if (meetingType) {
          if (this.zoomConnections?.length) {
            if (!this.meetingType.zoom_connection) {
              this.meetingType.zoom_connection =
                this.zoomConnections.find(zc => zc.default) ||
                this.zoomConnections[0];
            }
          }
          if (this.googleMeetConnections?.length) {
            if (!this.meetingType.zoom_connection) {
              this.meetingType.zoom_connection =
                this.zoomConnections.find(zc => zc.default) ||
                this.zoomConnections[0];
            }
          }
          if (this.googleCalendars?.length) {
            if (!this.meetingType.google_calendar) {
              this.meetingType.google_calendar =
                this.googleCalendars.find(gc => gc.default) ||
                this.googleCalendars[0];
            }
          }
        }
      },
      deep: true,
    },
    zoomConnections: {
      handler: function (zoomConnections) {
        if (!this.meetingType) return;
        if (!zoomConnections.length) return;
        if (this.meetingType.zoom_connection) return;
        this.meetingType.zoom_connection =
          zoomConnections.find(zc => zc.default) || zoomConnections[0];
      },
      immediate: true,
    },
    googleCalendars: {
      handler: function (googleCalendars) {
        if (!this.meetingType) return;
        if (!googleCalendars.length) return;
        if (this.meetingType.google_calendar) return;
        this.meetingType.google_calendar =
          googleCalendars.find(gc => gc.default) || googleCalendars[0];
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.published {
  color: $light-green;
}
.draft {
  color: $dark-gray;
}
.copy {
  cursor: pointer;
  color: $medium-gray;
  &:hover {
    color: $dark-gray;
  }
}

.question-outline {
  max-width: 100%;
  //width: 300px;
  cursor: pointer;

  &.active {
    background-color: $lightest-gray;
  }
  &:hover {
    background-color: $lightest-gray;
  }

  &.default {
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
    }
  }

  .delete {
    &:hover {
      background-color: $lightest-gray;
    }
  }
}

::v-deep .hm-upgrade-lock {
  #upgrade-trigger {
    width: 24px;
    height: 24px;
    font-size: 0.8rem;
  }
}
.www-card {
  .www-logo {
    max-width: 300px;
  }

  background-color: lighten($coral, 25%) !important;
}
</style>
